
(() => {
  const config = window.BhetwaleWholesalePdfConfig || {};
  const root = document.getElementById(config.rootId || '');
  if (!root || root.dataset.pdfReady === 'true') return;
  root.dataset.pdfReady = 'true';

  const button = root.querySelector('.bhetwale-wholesale-pdf__button');
  const status = root.querySelector('.bhetwale-wholesale-pdf__status');
  const collectionHandle = config.collectionHandle || '';
  const collectionTitle = config.collectionTitle || '';
  const origin = window.location.origin;
  const slabOverrides = config.slabOverrides || {};

  const K = {
    black: [5, 5, 5],
    ink: [24, 24, 24],
    red: [254, 182, 9],
    red2: [254, 200, 45],
    white: [255, 255, 255],
    grey: [104, 104, 104],
    light: [246, 246, 246],
    line: [225, 225, 225]
  };

  const clean = (v) => String(v == null ? '' : v)
    .replace(/×/g, 'x')
    .replace(/[–—]/g, '-')
    .replace(/₹/g, 'Rs. ')
    .replace(/•/g, '-')
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const asNumber = (v) => {
    if (v == null || v === '') return null;
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };

  const money = (value) => {
    const n = asNumber(value);
    if (n == null) return '-';
    const rounded = Math.round(n * 100) / 100;
    return Number.isInteger(rounded) ? `Rs. ${rounded}` : `Rs. ${rounded.toFixed(2)}`;
  };

  const tierFromBase = (baseRupees, factor) => {
    const basePaise = Math.round(Number(baseRupees) * 100);
    if (!Number.isFinite(basePaise)) return null;
    return (basePaise - Math.floor((basePaise * factor) / 100000)) / 100;
  };

  const pricingFor = (product, variant) => {
    const base = asNumber(variant && variant.price);
    const o = slabOverrides[product.handle] || {};
    const meaningfulVariants = (product.variants || []).filter(v => clean(v.title) && clean(v.title) !== 'Default Title');
    const useProductOverrides = meaningfulVariants.length <= 1;
    return {
      moq: Number(o.moq) || 10,
      p10: base,
      p50: useProductOverrides && asNumber(o.p50) != null ? asNumber(o.p50) : tierFromBase(base, 3846),
      p100: useProductOverrides && asNumber(o.p100) != null ? asNumber(o.p100) : tierFromBase(base, 7692),
      p250: useProductOverrides && asNumber(o.p250) != null ? asNumber(o.p250) : tierFromBase(base, 11538)
    };
  };

  const loadScript = async () => {
    if (window.jspdf && window.jspdf.jsPDF) return;
    const sources = [
      'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
    ];
    let lastError = null;
    for (const src of sources) {
      try {
        await new Promise((resolve, reject) => {
          const existing = document.querySelector(`script[src="${src}"]`);
          if (existing) {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
            return;
          }
          const s = document.createElement('script');
          s.src = src;
          s.async = true;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
        if (window.jspdf && window.jspdf.jsPDF) return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error('PDF library unavailable');
  };

  const fetchProducts = async () => {
    const all = [];
    for (let page = 1; page <= 20; page++) {
      status.textContent = `Loading catalogue products... ${all.length}`;
      const r = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250&page=${page}`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('Could not load products');
      const data = await r.json();
      const batch = Array.isArray(data.products) ? data.products : [];
      all.push(...batch);
      if (batch.length < 250) break;
    }
    return all;
  };

  const imageCache = new Map();
  const imageUrlFrom = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') return entry;
    return entry.src || entry.url || null;
  };

  const imageToJpeg = async (rawUrl) => {
    if (!rawUrl) return null;
    const url = rawUrl.startsWith('//') ? `https:${rawUrl}` : rawUrl;
    if (imageCache.has(url)) return imageCache.get(url);
    const task = (async () => {
      try {
        const r = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!r.ok) return null;
        const blob = await r.blob();
        const objectUrl = URL.createObjectURL(blob);
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = objectUrl;
        });
        const max = 720;
        const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(objectUrl);
        return canvas.toDataURL('image/jpeg', 0.82);
      } catch (e) {
        return null;
      }
    })();
    imageCache.set(url, task);
    return task;
  };

  const htmlSpecs = (html) => {
    const specs = {};
    try {
      const parsed = new DOMParser().parseFromString(html || '', 'text/html');
      const candidates = [...parsed.querySelectorAll('li')].map(el => clean(el.textContent));
      candidates.forEach(line => {
        const m = line.match(/^([^:]{2,30}):\s*(.+)$/);
        if (!m) return;
        const rawKey = m[1].trim().toLowerCase();
        const value = m[2].trim();
        let key = null;
        if (rawKey.includes('material')) key = 'Material';
        else if (rawKey === 'size' || rawKey.includes('dimension')) key = 'Size';
        else if (rawKey.includes('colour') || rawKey.includes('color') || rawKey.includes('finish')) key = 'Colour';
        else if (rawKey.includes('include')) key = 'Includes';
        else if (rawKey.includes('capacity')) key = 'Capacity';
        else if (rawKey.includes('moq') || rawKey.includes('minimum')) key = 'MOQ';
        else if (rawKey.includes('branding')) key = 'Branding';
        if (key && !specs[key]) specs[key] = value;
      });
    } catch (e) {}
    return specs;
  };

  const drawContainedImage = (doc, jpeg, x, y, w, h) => {
    doc.setFillColor(...K.light);
    doc.roundedRect(x, y, w, h, 2.4, 2.4, 'F');
    if (!jpeg) return false;
    const props = doc.getImageProperties(jpeg);
    const ratio = Math.min((w - 2) / props.width, (h - 2) / props.height);
    const dw = props.width * ratio;
    const dh = props.height * ratio;
    doc.addImage(jpeg, 'JPEG', x + (w - dw) / 2, y + (h - dh) / 2, dw, dh, undefined, 'FAST');
    return true;
  };

  const drawMissingView = (doc, x, y, w, h) => {
    doc.setFillColor(...K.black);
    doc.roundedRect(x, y, w, h, 2.4, 2.4, 'F');
    doc.setTextColor(...K.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('BHETWALE', x + w / 2, y + h / 2 - 2, { align: 'center' });
    doc.setTextColor(180, 180, 180);
    doc.setFontSize(6.8);
    doc.setFont('helvetica', 'normal');
    doc.text('SECOND VIEW COMING SOON', x + w / 2, y + h / 2 + 5, { align: 'center' });
  };

  const drawPageChrome = (doc, pageNo, totalPagesLabel = '') => {
    doc.setFillColor(...K.white);
    doc.rect(0, 0, 210, 297, 'F');
    doc.setFillColor(...K.black);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setFillColor(...K.red);
    doc.rect(0, 22, 210, 1.4, 'F');
    doc.setTextColor(...K.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13.5);
    doc.text('BHETWALE', 14, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.3);
    doc.setTextColor(205, 205, 205);
    doc.text('AAPKI KHUSHIYON KA PITARA', 43, 14);
    doc.text(clean(collectionTitle).toUpperCase(), 196, 14, { align: 'right' });
    doc.setTextColor(...K.grey);
    doc.setFontSize(6.6);
    doc.text('B2B WHOLESALE CATALOGUE', 14, 291);
    doc.text(`${pageNo}${totalPagesLabel}`, 196, 291, { align: 'right' });
  };

  const drawCover = async (doc, products) => {
    doc.setFillColor(...K.black);
    doc.rect(0, 0, 210, 297, 'F');
    doc.setFillColor(...K.red);
    doc.rect(0, 0, 7, 297, 'F');

    doc.setTextColor(...K.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.text('BHETWALE', 20, 38);
    doc.setTextColor(...K.red2);
    doc.setFontSize(8.5);
    doc.text('AAPKI KHUSHIYON KA PITARA', 21, 48);

    doc.setTextColor(...K.white);
    doc.setFontSize(39);
    doc.text('DIWALI', 20, 86);
    doc.setFontSize(22);
    doc.text('WHOLESALE CATALOGUE', 20, 101);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(185, 185, 185);
    doc.text('2026 EDITION / B2B GIFTING & PACKAGING', 21, 113);

    const statY = 137;
    const stats = [
      [`${products.length}`, 'PRODUCTS'],
      ['MOQ 10', 'STARTING QUANTITY'],
      ['4-TIER', 'VOLUME PRICING']
    ];
    stats.forEach((s, i) => {
      const x = 20 + i * 59;
      doc.setDrawColor(70, 70, 70);
      doc.line(x, statY - 5, x + 48, statY - 5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(...K.white);
      doc.text(s[0], x, statY + 6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(155, 155, 155);
      doc.text(s[1], x, statY + 13);
    });

    const picks = [products[0], products[Math.floor(products.length / 2)], products[products.length - 1]].filter(Boolean);
    const jpgs = await Promise.all(picks.map(p => imageToJpeg(imageUrlFrom((p.images || [])[0]))));
    const imgY = 169, imgW = 54, imgH = 76, gap = 7;
    jpgs.forEach((jpg, i) => {
      const x = 20 + i * (imgW + gap);
      doc.setFillColor(20, 20, 20);
      doc.roundedRect(x, imgY, imgW, imgH, 2, 2, 'F');
      if (jpg) drawContainedImage(doc, jpg, x, imgY, imgW, imgH);
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(...K.white);
    doc.text('CURRENT PRODUCTS. CURRENT RATES. READY TO SHARE.', 20, 264);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.1);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated ${new Date().toLocaleDateString('en-IN')} from the live Bhetwale Diwali collection.`, 20, 274);
    doc.text('GST, shipping, filling and branding are extra unless specifically mentioned.', 20, 282);
  };

  const drawSpecGrid = (doc, product, x, y, w) => {
    const specs = htmlSpecs(product.body_html);
    const preferred = ['Material', 'Size', 'Colour', 'Includes', 'Capacity'];
    const rows = preferred.filter(k => specs[k]).slice(0, 4).map(k => [k, specs[k]]);
    if (!rows.length) rows.push(['Category', 'Premium Diwali Gifting']);
    const colW = w / 2;
    rows.forEach((row, i) => {
      const col = i % 2;
      const r = Math.floor(i / 2);
      const sx = x + col * colW;
      const sy = y + r * 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.4);
      doc.setTextColor(...K.red);
      doc.text(clean(row[0]).toUpperCase(), sx, sy);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.1);
      doc.setTextColor(...K.ink);
      const lines = doc.splitTextToSize(clean(row[1]), colW - 6).slice(0, 2);
      doc.text(lines, sx, sy + 4);
    });
  };

  const drawSinglePricing = (doc, product, variant, x, y, w) => {
    const price = pricingFor(product, variant);
    const labels = [`${price.moq}-49 PCS`, '50-99 PCS', '100-249 PCS', '250+ PCS'];
    const values = [price.p10, price.p50, price.p100, price.p250];
    const gap = 2.5;
    const cw = (w - gap * 3) / 4;
    labels.forEach((label, i) => {
      const cx = x + i * (cw + gap);
      const isBest = i === 3;
      doc.setFillColor(...(isBest ? K.red : K.light));
      doc.roundedRect(cx, y, cw, 19, 1.8, 1.8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5.9);
      doc.setTextColor(...(isBest ? K.white : K.grey));
      doc.text(label, cx + 3, y + 6);
      doc.setFontSize(9.3);
      doc.setTextColor(...(isBest ? K.white : K.ink));
      doc.text(money(values[i]), cx + 3, y + 13.8);
      if (isBest) {
        doc.setFontSize(4.8);
        doc.setTextColor(255, 205, 210);
        doc.text('BEST VALUE', cx + cw - 3, y + 6, { align: 'right' });
      }
    });
  };

  const drawVariantPricing = (doc, product, variants, x, y, w) => {
    const labelW = 38;
    const priceW = (w - labelW) / 4;
    const rowH = 8.5;
    const headers = ['VARIANT', '10-49', '50-99', '100-249', '250+'];
    doc.setFillColor(...K.black);
    doc.roundedRect(x, y, w, rowH, 1.5, 1.5, 'F');
    headers.forEach((h, i) => {
      const hx = i === 0 ? x + 3 : x + labelW + (i - 1) * priceW + priceW / 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5.6);
      doc.setTextColor(...K.white);
      doc.text(h, hx, y + 5.5, i === 0 ? {} : { align: 'center' });
    });
    variants.slice(0, 3).forEach((v, r) => {
      const ry = y + rowH + r * rowH;
      doc.setFillColor(...(r % 2 ? K.white : K.light));
      doc.rect(x, ry, w, rowH, 'F');
      const p = pricingFor(product, v);
      const vals = [p.p10, p.p50, p.p100, p.p250];
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.2);
      doc.setTextColor(...K.ink);
      doc.text(clean(v.title).slice(0, 22), x + 3, ry + 5.5);
      vals.forEach((val, i) => {
        doc.setFont('helvetica', i === 3 ? 'bold' : 'normal');
        doc.setFontSize(6.1);
        doc.setTextColor(...(i === 3 ? K.red : K.ink));
        doc.text(money(val), x + labelW + i * priceW + priceW / 2, ry + 5.5, { align: 'center' });
      });
    });
  };

  const drawProductBlock = async (doc, product, index, total, x, y, w, h) => {
    doc.setDrawColor(...K.line);
    doc.setLineWidth(.35);
    doc.roundedRect(x, y, w, h, 3, 3, 'S');
    doc.setFillColor(...K.red);
    doc.rect(x, y, 2.2, h, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.8);
    doc.setTextColor(...K.red);
    doc.text(`PRODUCT ${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`, x + 6, y + 7);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.7);
    doc.setTextColor(...K.ink);
    const titleLines = doc.splitTextToSize(clean(product.title), w - 13).slice(0, 3);
    doc.text(titleLines, x + 6, y + 14);
    const titleBottom = y + 14 + (titleLines.length - 1) * 4.1;

    const imgY = Math.max(y + 26, titleBottom + 5);
    const imgGap = 4;
    const imgW = (w - 16 - imgGap) / 2;
    const imgH = 42;
    const images = product.images || [];
    const firstTwo = [imageUrlFrom(images[0]), imageUrlFrom(images[1])];
    const jpgs = await Promise.all(firstTwo.map(imageToJpeg));
    drawContainedImage(doc, jpgs[0], x + 6, imgY, imgW, imgH);
    if (jpgs[1]) drawContainedImage(doc, jpgs[1], x + 6 + imgW + imgGap, imgY, imgW, imgH);
    else drawMissingView(doc, x + 6 + imgW + imgGap, imgY, imgW, imgH);

    const specY = imgY + imgH + 8;
    drawSpecGrid(doc, product, x + 6, specY, w - 12);

    const meaningfulVariants = (product.variants || []).filter(v => clean(v.title) && clean(v.title) !== 'Default Title');
    const pricingY = specY + 22;
    if (meaningfulVariants.length > 1) drawVariantPricing(doc, product, meaningfulVariants, x + 6, pricingY, w - 12);
    else drawSinglePricing(doc, product, (product.variants || [])[0], x + 6, pricingY, w - 12);

    const footerY = y + h - 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(...K.grey);
    doc.text('Prices / pc. GST, shipping, filling & branding extra unless stated.', x + 6, footerY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...K.red);
    doc.text('VIEW PRODUCT', x + w - 6, footerY, { align: 'right' });
    const productUrl = `${origin}/products/${product.handle}`;
    doc.link(x + w - 34, footerY - 4, 28, 6, { url: productUrl });
  };

  const drawNotes = (doc, pageNo) => {
    drawPageChrome(doc, pageNo);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...K.ink);
    doc.text('WHOLESALE NOTES', 18, 50);
    doc.setFillColor(...K.red);
    doc.rect(18, 57, 32, 2, 'F');

    const notes = [
      'Minimum order quantity is 10 pieces unless a product specifically states otherwise.',
      '10-49, 50-99, 100-249 and 250+ rates are generated from Bhetwale current Shopify pricing logic at download time.',
      'Where a product has custom Bhetwale slab-rate metafields, those custom rates override the standard calculated tiers.',
      'GST, shipping, filling, branding and customisation charges are extra unless specifically mentioned.',
      'Product colours may vary slightly due to photography, printing batches and screen settings.',
      'For custom branding, large projects or special requirements, contact Bhetwale through the website / WhatsApp inquiry flow.'
    ];

    let y = 76;
    notes.forEach((note, i) => {
      doc.setFillColor(...K.red);
      doc.circle(22, y - 1.8, 1.4, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.2);
      doc.setTextColor(...K.red);
      doc.text(String(i + 1).padStart(2, '0'), 29, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.2);
      doc.setTextColor(...K.ink);
      const lines = doc.splitTextToSize(clean(note), 145);
      doc.text(lines, 42, y);
      y += lines.length * 5 + 12;
    });

    doc.setFillColor(...K.black);
    doc.roundedRect(18, 213, 174, 45, 4, 4, 'F');
    doc.setTextColor(...K.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('BHETWALE', 29, 232);
    doc.setTextColor(...K.red2);
    doc.setFontSize(8);
    doc.text('AAPKI KHUSHIYON KA PITARA', 29, 242);
    doc.setTextColor(185, 185, 185);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('Premium B2B gifting & packaging. Built for curators, resellers and bulk buyers.', 29, 250);
  };

  button.addEventListener('click', async () => {
    if (button.disabled) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'BUILDING PREMIUM PDF...';
    status.textContent = 'Loading current products, images and slab rates...';

    try {
      const [products] = await Promise.all([
        fetchProducts(),
        loadScript()
      ]);
      if (!products.length) throw new Error('No products found');
      if (!window.jspdf || !window.jspdf.jsPDF) throw new Error('PDF library unavailable');

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
      doc.setProperties({
        title: `Bhetwale ${clean(collectionTitle)} Wholesale Catalogue 2026`,
        subject: 'Bhetwale B2B wholesale catalogue',
        author: 'Bhetwale',
        creator: 'Bhetwale Shopify Catalogue Generator'
      });

      status.textContent = 'Designing cover...';
      await drawCover(doc, products);

      const blockX = 12;
      const blockW = 186;
      const blockH = 122;
      const firstY = 30;
      const gapY = 8;
      let pageNo = 1;

      for (let i = 0; i < products.length; i += 2) {
        doc.addPage();
        pageNo += 1;
        drawPageChrome(doc, pageNo);
        status.textContent = `Designing products ${i + 1}-${Math.min(i + 2, products.length)} of ${products.length}...`;
        await drawProductBlock(doc, products[i], i, products.length, blockX, firstY, blockW, blockH);
        if (products[i + 1]) {
          await drawProductBlock(doc, products[i + 1], i + 1, products.length, blockX, firstY + blockH + gapY, blockW, blockH);
        }
      }

      doc.addPage();
      pageNo += 1;
      drawNotes(doc, pageNo);

      const fileName = `Bhetwale-${clean(collectionTitle).replace(/[^A-Za-z0-9]+/g, '-')}-Premium-Wholesale-Catalogue-2026.pdf`;
      doc.save(fileName);
      status.textContent = `Premium PDF downloaded • ${products.length} products • 4-tier rates`;
    } catch (err) {
      console.error('Bhetwale premium PDF error', err);
      status.textContent = 'Could not generate PDF. Please try again.';
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  });
})();